import { PermissionEnum } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import StepOne from '../../../../components/new-application/individual/step-one';
import StepItem from '../../../../components/new-application/common/steps/step-item';
import StepThree from '../../../../components/new-application/individual/step-three';
import Agreements from '../../../../components/new-application/common/agreements/agreements';
import OngoingMonitoring from '../../common/ongoing-monitoring';
import { NewApplicationStepStatus } from '../../types';
import { onGroupSelect, setSelected } from '../store/slices';
import { ApplicationStepsTypes } from './types';

const ApplicationSteps = ({
  applicationStepsStatus,
  control,
  questionnarieControl,
  errors,
  questionnarieErrors,
  newApplicationInfo,
  activePermissions,
  clearErrors,
  questionnarieClearErrors,
  questionnarieSetValue,
  documents,
  fields,
  questionnarieFields,
  sourceOfWealth,
  sourceOfIncome,
  sourceOfWealthForIndividual,
  affilatedCompaniesValues,
  setAffilatedCompaniesValues,
  affilatedCompaniesIsLoading,
  affilatedCompaniesIsFetching,
  deleteAffilatedCompanyInProgress,
  onDeleteAffilatedCompany,
  selectedAffilatedCompanyId,
  applicationId,
  crsAndFatcaIsLoading,
  crsAndFatcaIsFetching,
  deleteCrsAndFatcaInProgress,
  onDeleteCRSAndFatca,
  crsAndFatcaValues,
  setCrsAndFatcaValues,
  setHaveSecondPassport,
  haveSecondPassport,
  selectedCrsAndFatcaId,
  setValue,
  search,
  ongoingMonitoring,
  ongoingCheckingStatus,
  agreements,
  agreementsIsLoading,
  questionnarieIsLoading,
}: ApplicationStepsTypes) => {
  const {
    baseCurrency,
    countriesList: countries,
    settings,
  } = useAppSelector((state) => state.root);
  const { selectedDocumentsCounts, allowedDocumentsCategories } =
    useAppSelector((state) => state.newApplications.mainRepresentative);

  const dispatch = useAppDispatch();

  const uboPercentage = settings?.find(
    (item) => item.key === 'MINIMUM-UBO-OWNERSHIP-PERCENTAGE',
  )?.data?.value;

  return (
    <>
      <StepItem
        title="Step 1"
        status={applicationStepsStatus?.stepOne as NewApplicationStepStatus}
        id="step-1"
      >
        <StepOne
          control={control}
          questionnarieControl={questionnarieControl}
          errors={errors}
          questionnarieErrors={questionnarieErrors}
          status={newApplicationInfo?.status}
          isEditable={
            activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            ) && newApplicationInfo?.basePersonalInfoEditable
          }
          activePermissions={activePermissions}
          hasSumSubId={Boolean(
            newApplicationInfo?.sumSubApplication?.applicantId,
          )}
          applicationScheme={newApplicationInfo?.applicationScheme}
          clearErrors={clearErrors}
          questionnarieClearErrors={questionnarieClearErrors}
          questionnarieSetValue={questionnarieSetValue}
          groupStructurChartFile={documents?.find(
            (item) =>
              item.documentCategoryId ===
              '72b810fc-d635-4937-8a14-f9b77eaa699f',
          )}
          cofRegulatedCompanyScheme={
            newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159'
          }
          clientActivity={fields.clientActivityId}
          questionnarieFields={questionnarieFields}
          sourceOfWealth={sourceOfWealth}
          baseCurrency={baseCurrency}
          sourceOfIncome={sourceOfIncome}
          sourceOfWealthForIndividual={sourceOfWealthForIndividual}
          fields={fields}
          affilatedCompaniesValues={affilatedCompaniesValues}
          setAffilatedCompaniesValues={setAffilatedCompaniesValues}
          affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
          affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
          deleteAffilatedCompanyInProgress={deleteAffilatedCompanyInProgress}
          onDeleteAffilatedCompany={onDeleteAffilatedCompany}
          selectedAffilatedCompanyId={selectedAffilatedCompanyId}
          uboPercentage={uboPercentage as string}
          questionnarieIsLoading={questionnarieIsLoading}
          companyOfficial
        />
      </StepItem>
      {(!newApplicationInfo?.applicationScheme ||
        (newApplicationInfo.applicationScheme &&
          newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')) && (
        <StepItem
          title="Step 2"
          status={applicationStepsStatus?.stepTwo as NewApplicationStepStatus}
          id="step-2"
        >
          <StepThree
            sumSubStatus={newApplicationInfo?.sumSubApplication?.reviewAnswer}
            sumSubReviewStatus={
              newApplicationInfo?.sumSubApplication?.reviewStatus
            }
            changeLogsAvailable={activePermissions?.includes(
              PermissionEnum.AUDIT_LOG_LIST,
            )}
            sumSubId={newApplicationInfo?.sumSubApplication?.id}
            id={newApplicationInfo?.sumSubApplication?.applicantId as string}
            countries={countries}
            control={control}
            documents={documents}
            documentsVisible={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
            )}

            isEditable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            )}
            selectedDocumentsCounts={selectedDocumentsCounts}
            status={newApplicationInfo?.status}
            applicationId={applicationId}
            onGroupSelect={(data) => dispatch(onGroupSelect(data))}
            setSelected={({ id, selected }) =>
              dispatch(setSelected({ id, selected }))
            }
            sumSubDocuments={newApplicationInfo?.idDocs?.edges?.map(
              (item) => item.node,
            )}
            search={search}
            errors={errors}
            renameAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
            )}
            questionnarieControl={questionnarieControl}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieErrors={questionnarieErrors}
            questionnarieFields={questionnarieFields}
            questionnarieSetValue={questionnarieSetValue}
            crsAndFatcaIsLoading={crsAndFatcaIsLoading}
            crsAndFatcaIsFetching={crsAndFatcaIsFetching}
            deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
            onDeleteCRSAndFatca={onDeleteCRSAndFatca}
            crsAndFatcaValues={crsAndFatcaValues}
            setCrsAndFatcaValues={setCrsAndFatcaValues}
            clearErrors={clearErrors}
            setHaveSecondPassport={setHaveSecondPassport}
            haveSecondPassport={haveSecondPassport}
            cofRegulatedCompanyScheme={
              newApplicationInfo?.applicationScheme?.id ===
              'a87db4b4-3c78-4564-bba1-c16a19553159'
            }
            selectedCrsAndFatcaId={selectedCrsAndFatcaId}
            allowedDocumentsCategories={allowedDocumentsCategories}
            cofWithDocuments={Boolean(
              newApplicationInfo?.applicationScheme?.id ===
              'ad811a02-87ed-4e19-ad45-770c0fd8fe41',
            )}
            activePermissions={activePermissions}
            setValue={setValue}
            fields={fields}
            questionnarieIsLoading={questionnarieIsLoading}
            cof
          />
        </StepItem>
      )}
      <StepItem
        title={
          !newApplicationInfo?.applicationScheme ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
            ? 'Step 3'
            : 'Step 2'
        }
        status={applicationStepsStatus?.stepFour as NewApplicationStepStatus}
        id="step-3"
      >
        <OngoingMonitoring
          status={newApplicationInfo?.status}
          applicationId={newApplicationInfo?.id}
          ongoingMonitoring={ongoingMonitoring}
          ongoingCheckingStatus={ongoingCheckingStatus}
        />
      </StepItem>
      {(!newApplicationInfo?.applicationScheme ||
        (newApplicationInfo.applicationScheme &&
          newApplicationInfo.applicationScheme.agreementsActive) ||
        (newApplicationInfo.applicationScheme &&
          newApplicationInfo.applicationScheme.agreementsActive === null)) && (
        <StepItem
          title={
            !newApplicationInfo?.applicationScheme ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
              ? 'Step 4'
              : 'Step 3'
          }
          status={applicationStepsStatus?.stepThree as NewApplicationStepStatus}
          id="step-4"
        >
          <Agreements
            list={agreements}
            errors={errors}
            zIndex={17}
            agreementsIsLoading={agreementsIsLoading}
          />
        </StepItem>
      )}
    </>
  );
};

export default ApplicationSteps;
